import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { GET_USER_ACCOUNT_TOKENS } from "../../graphql/queries";
import { API_BASE } from "../../utils/api/api";
import styles from "./ConnectModal.module.css";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GOOGLE_REDIRECT_URI = `${API_BASE}/google/callback`;
const GOOGLE_SCOPES = [
  "openid",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
  "https://www.googleapis.com/auth/analytics.readonly",
  "https://www.googleapis.com/auth/webmasters.readonly",
  "https://www.googleapis.com/auth/webmasters",
  "https://www.googleapis.com/auth/adsense.readonly",
  "https://www.googleapis.com/auth/content",
  "https://www.googleapis.com/auth/siteverification",
].join(" ");

const GoogleConnectModal = ({ visible, onClose, onConnected }) => {
  const { t } = useLocalization();
  const { user } = useAuthUser();
  const authToken = user?.token;
  const userId = user?._id || user?.id || user?.userId;

  const [isConnecting, setIsConnecting] = useState(false);
  const [status, setStatus] = useState(null);
  const pollIntervalRef = useRef(null);
  const oauthWindowRef = useRef(null);

  const { data: tokensData, refetch: refetchTokens } = useQuery(
    GET_USER_ACCOUNT_TOKENS,
    {
      skip: !authToken,
      fetchPolicy: "network-only",
      context: {
        headers: { Authorization: authToken ? `Bearer ${authToken}` : "" },
      },
    }
  );

  const isGoogleConnected = Boolean(
    tokensData?.getUserAccountTokens?.googleToken ||
      tokensData?.getUserAccountTokens?.googleRefreshToken
  );

  // Poll for connection status while modal is visible and connecting
  useEffect(() => {
    if (!visible || !isConnecting) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const result = await refetchTokens();
        const connected = Boolean(
          result?.data?.getUserAccountTokens?.googleToken ||
            result?.data?.getUserAccountTokens?.googleRefreshToken
        );
        if (connected) {
          setIsConnecting(false);
          setStatus(t("GoogleConnectScreen.connected") || "Connected!");
          onConnected?.();
          setTimeout(() => onClose?.(), 1500);
        }
      } catch (e) {
        console.log("refetchTokens error:", e?.message || e);
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [visible, isConnecting, refetchTokens, onClose, onConnected, t]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const buildAuthUrl = useCallback(() => {
    const state = encodeURIComponent(userId || "");
    return (
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(GOOGLE_REDIRECT_URI)}` +
      `&response_type=code&scope=${encodeURIComponent(GOOGLE_SCOPES)}` +
      `&access_type=offline&prompt=consent&state=${state}`
    );
  }, [userId]);

  const startOAuth = useCallback(() => {
    if (!userId) {
      setStatus(t("GoogleConnectScreen.oauthUserMissing") || "User missing");
      setIsConnecting(false);
      return;
    }

    setIsConnecting(true);
    setStatus(t("GoogleConnectScreen.connectingFindability") || "Connecting...");

    const authUrl = buildAuthUrl();
    oauthWindowRef.current = window.open(authUrl, "_blank", "noopener,width=600,height=700");

    // Check if window was closed
    const checkClosed = setInterval(() => {
      if (oauthWindowRef.current?.closed) {
        clearInterval(checkClosed);
        // Keep polling for a bit after window closes
        setTimeout(() => {
          if (!isGoogleConnected) {
            setStatus(t("GoogleConnectScreen.checkingConnection") || "Checking connection...");
          }
        }, 1000);
      }
    }, 500);
  }, [userId, buildAuthUrl, t, isGoogleConnected]);

  if (!visible) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose} type="button">
          &times;
        </button>

        <div className={styles.iconBox} style={{ backgroundColor: "#EEF4FF" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        </div>

        <h2 className={styles.title}>
          {t("GoogleConnectScreen.headerTitle") || "Connect Google"}
        </h2>
        <p className={styles.subtitle}>
          {t("GoogleConnectScreen.subtitleDual") ||
            "Connect your Google account to enable analytics, search console, and merchant center integration."}
        </p>

        <button
          className={`${styles.primaryBtn} ${styles.googleBtn}`}
          onClick={startOAuth}
          disabled={isConnecting}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 8 }}>
            <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z" />
          </svg>
          {isConnecting
            ? t("GoogleConnectScreen.connectingFindability") || "Connecting..."
            : isGoogleConnected
            ? t("GoogleConnectScreen.connected") || "Google Connected"
            : t("GoogleConnectScreen.connectFindability") || "Connect Google"}
        </button>

        {(status || isConnecting) && (
          <div className={styles.statusRow}>
            {isConnecting && <div className={styles.spinner} />}
            {status && <span className={styles.statusText}>{status}</span>}
          </div>
        )}

        <div className={styles.tipBox}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#2563EB">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          <span className={styles.tipText}>
            {t("GoogleConnectScreen.tip") ||
              "This connects Google Analytics, Search Console, and Merchant Center to provide comprehensive insights."}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GoogleConnectModal;
